<!--
  The project dashboard: where this actually is, stated so it cannot be quoted out of context.
  It is the home page's default panel, so it leads with progress — that is what a returning
  annotator came for — and everything else follows in the order that makes progress readable.

  Five rules govern everything below, and each of them has a failure mode it exists to prevent:

  1. **Zero is a legitimate state.** There are genuinely no boxes yet. Every zero here has to
     read as "not started", never as an error and never as an empty void — so a zero comes with
     the denominator it will eventually fill and a sentence saying so.
  2. **Never a percentage without its denominator.** "12% complete" is exactly the number that
     ends up in a slide with no idea what it is 12% of. Every ratio is rendered as `n/total`,
     and a bar is only ever an accompaniment to the pair.
  3. **Colour is never the only signal.** Severities, splits and gaps all carry a word.
  4. **If the API is unreachable, or the tool is not loaded, say so.** A dashboard of zeros that
     looks like real data is worse than no dashboard.
  5. **A caveat is never buried.** The honesty flags come before the numbers they qualify, on
     the panel that is open by default. A `blocking` flag — "do not trust these numbers" — is
     rendered in full, always, with no disclosure around it; `info` and `caution` flags condense
     to one always-readable headline each, with their detail one click away *on this panel*.
     Reordering this page must never turn a caveat into something you have to go and find.
-->
<script lang="ts">
  import { untrack } from 'svelte';

  import type { ClassCoverage, HonestyFlag } from '../lib/api/types';
  import { percent } from '../lib/progress';
  import { href } from '../lib/router.svelte';
  import { stats } from '../lib/stores/stats.svelte';
  import ProgressMeter from './ProgressMeter.svelte';

  $effect(() => {
    untrack(() => void stats.ensure());
  });

  const data = $derived(stats.data);

  type ClassSort = 'vocab' | 'rarest';
  let classSort = $state<ClassSort>('vocab');

  /**
   * Sorted per-class rows. **Never filtered** — all 30 classes, zeros included. Dropping an
   * empty row is how a class with 42 corpus instances stops being visible as the permanent
   * constraint it is.
   */
  const classRows = $derived.by((): ClassCoverage[] => {
    const rows = [...(data?.classes ?? [])];
    if (classSort === 'rarest') {
      // Unknown ceiling (no priors.json) sorts last rather than as 0: "we do not know" is not
      // "there are none".
      rows.sort((a, b) => {
        const left = a.corpus_instances ?? Number.POSITIVE_INFINITY;
        const right = b.corpus_instances ?? Number.POSITIVE_INFINITY;
        return left - right || a.class_id - b.class_id;
      });
    }
    return rows;
  });

  const ceilingMax = $derived(
    Math.max(1, ...(data?.classes ?? []).map((row) => row.corpus_instances ?? 0)),
  );

  const SEVERITY_WORD: Record<HonestyFlag['severity'], string> = {
    info: 'For the record',
    caution: 'Known limitation',
    blocking: 'Do not trust these numbers',
  };

  /**
   * Blocking flags are rendered separately from the rest, and never inside a disclosure.
   *
   * "Do not trust these numbers" behind a summary the reader has to click is the same as not
   * saying it. So the two lists are built here rather than being one loop with a conditional
   * wrapper: there is no code path on which a blocking flag can end up collapsed.
   */
  const blockingFlags = $derived((data?.honesty ?? []).filter((flag) => flag.severity === 'blocking'));
  const noteFlags = $derived((data?.honesty ?? []).filter((flag) => flag.severity !== 'blocking'));

  /**
   * Whether the *notes'* detail is open. Headlines never depend on this — they are outside the
   * disclosure — so the closed state condenses the strip without hiding what it says.
   */
  let notesOpen = $state(false);

  function timestamp(value: string | null | undefined): string {
    if (!value) return 'unrecorded';
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toISOString().replace('T', ' ').slice(0, 19) + 'Z';
  }
</script>

<section class="dash col" aria-labelledby="dashboard-heading">
  <div class="row wrap head">
    <h2 id="dashboard-heading">Where the project is</h2>
    <span class="spacer"></span>
    {#if stats.loadedAt}
      <span class="small muted">
        as of {stats.loadedAt.toISOString().replace('T', ' ').slice(0, 19)}Z
      </span>
    {/if}
    <button onclick={() => void stats.load()} disabled={stats.loading}>
      {stats.loading ? 'Loading…' : 'Refresh'}
    </button>
  </div>
  <p class="small muted note">
    Read once when this page opens, and again only when you press Refresh. These are full-corpus
    aggregates that move when somebody draws a box, not on a timer.
  </p>

  {#if stats.problem}
    <div class="banner error col" role="alert">
      <strong>Could not read the project statistics</strong>
      <p class="small">{stats.problem.message}</p>
      <p class="small muted mono">code: {stats.problem.code}</p>
      <p class="small muted">
        Nothing below is being substituted with zeros. If the service is not running, start it
        from <span class="mono">tools/hismaic_annotator/</span> with
        <span class="mono">uv run uvicorn hismaic.api.main:app --port 8002</span>.
      </p>
    </div>
  {/if}

  {#if data && data.blockers.length}
    <div class="banner error col" role="alert">
      <strong>The tool is not ready to annotate — the numbers below are not a progress report</strong>
      <ul class="small">
        {#each data.blockers as blocker, index (index)}
          <li>{blocker}</li>
        {/each}
      </ul>
      <p class="small muted">
        Run the commands above from <span class="mono">tools/hismaic_annotator/</span>, or set a
        bucket in <a href={href({ name: 'settings' })}>Settings</a>.
      </p>
    </div>
  {/if}

  {#if !data}
    {#if !stats.problem}
      <p class="muted small">Loading the project statistics…</p>
    {/if}
  {:else}
    <!-- Honesty flags first. They qualify everything under them, so they cannot be a footer. -->
    {#if data.honesty.length}
      <div class="panel col" data-section="honesty">
        <h3>Read these before quoting anything below</h3>

        {#if blockingFlags.length}
          <!--
            Blocking: full text, always, no disclosure, styled as loudly as the configuration
            blockers above it. There is no `open` state to get wrong because there is no
            disclosure element in this branch at all.
          -->
          <ul class="flags col banner error" data-flags="blocking" role="alert">
            {#each blockingFlags as flag (flag.code)}
              <li
                class="flag blocking"
                data-flag={flag.code}
                data-flag-severity="blocking"
                data-collapsible="false"
              >
                <!-- The severity is a word, not a colour: a greyscale screenshot has to carry it. -->
                <span class="badge small mono">{SEVERITY_WORD.blocking}</span>
                <div class="col tight">
                  <strong class="small">{flag.headline}</strong>
                  <span class="small">{flag.detail}</span>
                </div>
              </li>
            {/each}
          </ul>
        {/if}

        {#if noteFlags.length}
          <div class="row wrap notes-head">
            <span class="small muted">
              {noteFlags.length} note(s) on how to read the figures below. Every headline is shown;
              the supporting detail expands here, on this panel.
            </span>
            <span class="spacer"></span>
            <button class="subtle small" onclick={() => (notesOpen = !notesOpen)}>
              {notesOpen ? 'Hide the detail' : 'Show all detail'}
            </button>
          </div>
          <ul class="flags col" data-flags="notes">
            {#each noteFlags as flag (flag.code)}
              <li
                class="flag {flag.severity}"
                data-flag={flag.code}
                data-flag-severity={flag.severity}
                data-collapsible="true"
              >
                <details open={notesOpen}>
                  <summary>
                    <span class="badge small mono">{SEVERITY_WORD[flag.severity]}</span>
                    <strong class="small headline">{flag.headline}</strong>
                  </summary>
                  <p class="small muted detail">{flag.detail}</p>
                </details>
              </li>
            {/each}
          </ul>
        {/if}
      </div>
    {/if}

    <!-- ------------------------------------------------------------------ -->
    <div class="panel col" data-section="progress">
      <h3>Annotation progress</h3>
      {#if data.progress.boxes_drawn === 0}
        <p class="small not-started">
          <span class="badge small mono">Not started</span>
          No boxes have been drawn yet. That is the expected state right now, not a failure: the
          corpus, the catalog, the vocabulary and the seed batch are in place and the first box is
          still to come.
        </p>
      {/if}
      <div class="meters">
        <ProgressMeter
          label="Boxes drawn, against the seed batch"
          value={data.progress.boxes_drawn}
          total={Math.max(data.seed.expected_boxes, data.progress.boxes_drawn)}
          detail={`${data.seed.expected_boxes} expected boxes across ${data.seed.inscriptions} seed inscriptions (${data.seed.images} images). The seed batch is the target for Phase 2, not the whole corpus.`}
        />
        <ProgressMeter
          label="Images completed"
          value={data.progress.images_done}
          total={data.progress.images_total}
          detail={`${data.progress.images_in_progress} in progress · ${data.progress.images_skipped} skipped · ${data.progress.images_not_pictured} not this record · ${data.progress.images_unannotated} untouched`}
        />
      </div>
      <table>
        <caption class="small muted">
          Every figure is a count. The corpus-wide denominator is the whole annotatable corpus
          and is not a Phase 2 target — it is here so the seed batch can be seen in proportion.
        </caption>
        <tbody>
          <tr>
            <th scope="row">Boxes on expected chips</th>
            <td class="mono">{data.progress.boxes_expected_aligned}</td>
            <td class="small muted">aligned to a letter in OCIANA's reading</td>
          </tr>
          <tr>
            <th scope="row">Insertions</th>
            <td class="mono">{data.progress.boxes_unexpected}</td>
            <td class="small muted">glyphs on the stone the reading does not contain</td>
          </tr>
          <tr>
            <th scope="row">Recorded as not present</th>
            <td class="mono">{data.progress.absences_recorded}</td>
            <td class="small muted">
              expected letters an annotator judged absent — resolved, not outstanding
            </td>
          </tr>
          <tr>
            <th scope="row">Expected letters, whole annotatable corpus</th>
            <td class="mono">{data.progress.expected_letters_queue}</td>
            <td class="small muted">
              queue-eligible inscriptions that have imagery ({data.corpus.queue_eligible} records)
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!--
      Everything below is the full report, and it is folded.

      Five panels, six tables, a thirty-row per-class table and two SHA-256 hashes. None of it
      is wrong and none of it should go — the per-class table in particular is mandated by
      `modeling-and-evaluation.md`, because an mAP driven by `l`/`n`/`b` is not a reading
      system and a rare class must never be averaged away. But this was the **landing page**,
      and it opened on all of it, every figure reading `0`.

      So the split is by question rather than by importance. Above: is the tool ready, and how
      far along is it. Here: the audit. One disclosure, not five — somebody who wants the
      detail wants the detail, and making them open the classes and then the provenance and
      then the corpus separately is a worse deal than one honest "there is more".
    -->
    <details class="full-report">
      <summary>
        <h3>The full report</h3>
        <span class="small muted">
          per annotator, splits, provenance, all {data.classes.length} classes, corpus hashes
        </span>
      </summary>

      <!-- ------------------------------------------------------------------ -->
      <div class="panel col" data-section="annotators">
        <h3>Per annotator</h3>
      <p class="small muted note">
        Every box carries a non-nullable <span class="mono">annotator_id</span>. Showing only a
        project total would throw away the thing that provenance exists for — being able to ask
        later whether an error correlates with who labelled it.
      </p>
      {#if data.annotators.length === 0}
        <p class="small muted">
          No annotator has registered an identity with the service yet. An id is required before
          anything can be drawn.
        </p>
      {:else}
        <table>
          <thead>
            <tr>
              <th>Annotator</th>
              <th>Kind</th>
              <th>Boxes</th>
              <th>On chips</th>
              <th>Insertions</th>
              <th>Images touched</th>
              <th>Last box</th>
            </tr>
          </thead>
          <tbody>
            {#each data.annotators as row (row.annotator_id)}
              <tr>
                <td>{row.name} <span class="muted mono small">({row.annotator_id})</span></td>
                <td class="small">{row.kind}</td>
                <td class="mono">{row.boxes}</td>
                <td class="mono">{row.boxes_expected_aligned}</td>
                <td class="mono">{row.boxes_unexpected}</td>
                <td class="mono">{row.images_touched}</td>
                <td class="small muted mono">{row.last_box_utc ? timestamp(row.last_box_utc) : '—'}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      {/if}
    </div>

    <!-- ------------------------------------------------------------------ -->
    <div class="panel col" data-section="seed">
      <h3>Seed batch and splits</h3>
      <p class="small muted note">
        Splits are assigned per siglum group, never per record and never per image: two images of
        one stone on opposite sides of the boundary is memorization that reads as generalization.
        {#if data.seed.unassigned_inscriptions}
          <strong>{data.seed.unassigned_inscriptions}</strong> seed record(s) carry no split yet.
        {/if}
      </p>
      <table>
        <thead>
          <tr>
            <th>Split</th>
            <th>Inscriptions</th>
            <th>Siglum groups</th>
            <th>Expected boxes</th>
            <th>Images</th>
            <th>Boxes drawn</th>
            <th>Longest inscription</th>
          </tr>
        </thead>
        <tbody>
          {#each data.seed.splits as row (row.split)}
            <tr>
              <th scope="row">{row.split}</th>
              <td class="mono">{row.inscriptions}/{data.seed.inscriptions}</td>
              <td class="mono">{row.siglum_groups}</td>
              <td class="mono">{row.expected_boxes}/{data.seed.expected_boxes}</td>
              <td class="mono">{row.images}</td>
              <td class="mono">{row.boxes_drawn}</td>
              <td class="mono">
                {row.longest_inscription_tokens}
                {#if row.inscriptions && row.longest_inscription_tokens < data.seed.dense_inscription_tokens}
                  <span class="tag warn" title="No inscription in this split reaches the dense threshold">
                    under {data.seed.dense_inscription_tokens} — no dense text
                  </span>
                {/if}
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>

    <!-- ------------------------------------------------------------------ -->
    <div class="panel col" data-section="provenance">
      <h3>Provenance mix</h3>
      <p class="small muted note">
        All human today. The breakdown exists before Phase 5 makes it interesting, because a mix
        that only appears once it is non-trivial is a mix nobody has been reading.
      </p>
      <table>
        <tbody>
          <tr>
            <th scope="row">human</th>
            <td class="mono">{data.provenance.human}/{data.provenance.total}</td>
            <td class="small muted">drawn by a person</td>
          </tr>
          <tr>
            <th scope="row">model</th>
            <td class="mono">{data.provenance.model}/{data.provenance.total}</td>
            <td class="small muted">
              proposed by a model run — including proposals a human accepted unchanged
            </td>
          </tr>
          <tr>
            <th scope="row">model_corrected</th>
            <td class="mono">{data.provenance.model_corrected}/{data.provenance.total}</td>
            <td class="small muted">a model proposal a human edited</td>
          </tr>
          <tr>
            <th scope="row">non-human boxes in test</th>
            <td class="mono">
              {data.provenance.test_split_non_human_boxes}/{data.provenance.test_split_boxes}
            </td>
            <td class="small muted">must stay 0 — test is the permanent human-only anchor</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ------------------------------------------------------------------ -->
    <div class="panel col" data-section="classes">
      <div class="row wrap">
        <h3>Per-class coverage — all {data.classes.length} classes</h3>
        <span class="spacer"></span>
        <label class="row small">
          Order
          <select bind:value={classSort} aria-label="Class table ordering">
            <option value="vocab">vocab id (the model's output head)</option>
            <option value="rarest">rarest in the corpus first</option>
          </select>
        </label>
      </div>
      <p class="small muted note">
        Every class is listed, including the ones at zero. <strong>Corpus instances is a
        ceiling</strong>: a class with 42 instances in the whole corpus can never have more than
        42 real examples, however much annotation is done, so its per-class recall will always
        rest on a handful. That is a fact about the corpus, not something more work fixes — and
        it is why an aggregate mAP driven by <span class="glyph">l</span>,
        <span class="glyph">n</span> and <span class="glyph">b</span> would say nothing about
        whether this reads Hismaic.
        {#if !data.priors.available}
          Ceilings are unavailable in this installation, so that column reads "unknown" rather
          than 0.
        {/if}
      </p>
      <table class="classes">
        <thead>
          <tr>
            <th>id</th>
            <th>class</th>
            <th>kind</th>
            <th>corpus instances (ceiling)</th>
            <th>seed chips</th>
            <th>train</th>
            <th>val</th>
            <th>test</th>
            <th>boxes drawn</th>
            <th>gaps</th>
          </tr>
        </thead>
        <tbody>
          {#each classRows as row (row.class_id)}
            <tr class:zero={row.boxes_drawn === 0}>
              <td class="mono">{row.class_id}</td>
              <td class="glyph letter">{row.display}</td>
              <td class="small muted">{row.kind}</td>
              <td>
                {#if row.corpus_instances === null}
                  <span class="small muted">unknown</span>
                {:else}
                  <span class="row ceiling">
                    <span class="mono num">{row.corpus_instances}</span>
                    <!-- Decoration only: the number to its left is the readout. -->
                    <span class="bar" aria-hidden="true">
                      <span class="fill" style:width={`${percent(row.corpus_instances, ceilingMax)}%`}></span>
                    </span>
                  </span>
                {/if}
              </td>
              <td class="mono">{row.seed_expected}</td>
              <td class="mono">{row.seed_expected_by_split['train'] ?? 0}</td>
              <td class="mono">{row.seed_expected_by_split['val'] ?? 0}</td>
              <td class="mono">{row.seed_expected_by_split['test'] ?? 0}</td>
              <td class="mono">{row.boxes_drawn}</td>
              <td class="small">
                {#if row.missing_from_splits.length && data.seed.inscriptions}
                  <span class="tag warn" title="No expected instance of this class in that split">
                    absent from {row.missing_from_splits.join(', ')}
                  </span>
                {:else}
                  <span class="muted">—</span>
                {/if}
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
      {#if data.priors.available}
        <p class="small muted note">
          Ceilings come from <span class="mono">data/priors.json</span>, counted over the
          {data.priors.counted_over_records} records the vocabulary build included
          ({data.priors.total_instances} token instances in total). Records with no
          transliteration, a zero-letter text, a scrape-doubled line, a disputed reading or a
          foreign script are excluded and are listed in the vocab report.
        </p>
      {/if}
    </div>

    <!-- ------------------------------------------------------------------ -->
    <div class="panel col" data-section="corpus">
      <h3>Corpus and catalog</h3>
      <div class="figures">
        <div class="figure-cell">
          <span class="big mono">{data.corpus.inscriptions}</span>
          <span class="small muted">OCIANA records parsed</span>
        </div>
        <div class="figure-cell">
          <span class="big mono">{data.corpus.queue_eligible}/{data.corpus.inscriptions}</span>
          <span class="small muted">queue-eligible</span>
        </div>
        <div class="figure-cell">
          <span class="big mono">{data.corpus.images}</span>
          <span class="small muted">
            images linked ({data.corpus.images_orphan} orphan · {data.corpus.images_ambiguous}
            ambiguous)
          </span>
        </div>
        <div class="figure-cell">
          <span class="big mono">{data.corpus.vocab_classes}</span>
          <span class="small muted">vocabulary classes</span>
        </div>
      </div>

      <h4 class="small">Why {data.queue_blocks.blocked_inscriptions} records cannot be queued</h4>
      <table>
        <thead>
          <tr>
            <th>Reason</th>
            <th>Inscriptions</th>
          </tr>
        </thead>
        <tbody>
          {#each data.queue_blocks.reasons as row (row.reason)}
            <tr>
              <th scope="row" class="mono">{row.reason}</th>
              <td class="mono">{row.inscriptions}</td>
            </tr>
          {/each}
          {#each data.queue_blocks.corpus_exclusion_reasons as row (row.reason)}
            <tr class="sub">
              <th scope="row" class="mono">↳ {row.reason}</th>
              <td class="mono">{row.inscriptions}</td>
            </tr>
          {/each}
        </tbody>
      </table>
      <p class="small muted note" data-reconciliation>
        These columns deliberately do not sum to
        {data.queue_blocks.blocked_inscriptions}: the reasons total
        {data.queue_blocks.reason_occurrences} because
        {data.queue_blocks.multi_reason_inscriptions} record(s) carry more than one (no imagery
        <em>and</em> a corpus exclusion). The indented rows break down
        <span class="mono">corpus_excluded</span> and are already counted in it.
      </p>

      <dl class="provenance small">
        <dt>corpus sha256</dt>
        <dd class="mono">{data.corpus.corpus_sha256 ?? 'unrecorded'}</dd>
        <dt>vocab sha256</dt>
        <dd class="mono">{data.corpus.vocab_sha256 ?? 'unrecorded'}</dd>
        <dt>catalog built</dt>
        <dd class="mono">
          {timestamp(data.corpus.catalog_generated_utc)} from
          {data.corpus.catalog_image_source ?? 'an unrecorded source'}
          {#if data.corpus.catalog_image_origin}({data.corpus.catalog_image_origin}){/if}
        </dd>
      </dl>
      </div>
    </details>
  {/if}
</section>

<style>
  .dash {
    gap: 12px;
  }

  .head {
    align-items: baseline;
  }

  .note {
    margin: 0;
    max-width: 90ch;
  }

  .full-report > summary {
    cursor: pointer;
    padding: 6px 0;
  }

  /* `<h3>` inside the summary so the section keeps a place in the heading outline whether it
     is open or shut; inline so it shares the marker's line. */
  .full-report > summary h3 {
    display: inline;
    margin: 0;
  }

  .full-report > summary > .muted {
    margin-left: 8px;
  }

  .full-report[open] {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .meters {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 14px;
  }

  .tight {
    gap: 2px;
  }

  .flags {
    list-style: none;
    margin: 0;
    padding: 0;
    gap: 8px;
  }

  .flag {
    border-left: 3px solid var(--line-strong);
    padding-left: 10px;
  }

  .flag.blocking {
    display: grid;
    grid-template-columns: 190px 1fr;
    gap: 10px;
    align-items: start;
    border-left-color: var(--danger);
  }

  .flag.caution {
    border-left-color: var(--warn);
  }

  .flags.banner.error {
    /* The blocking list is a banner in its own right; the list reset keeps the bullets off. */
    list-style: none;
    padding: 8px 10px;
  }

  .notes-head {
    align-items: baseline;
    gap: 8px;
  }

  .flag summary {
    display: flex;
    gap: 10px;
    align-items: baseline;
    flex-wrap: wrap;
    cursor: pointer;
    list-style: none;
  }

  /* `display: flex` on a summary suppresses the browser's disclosure triangle, so the
     affordance is spelled out instead — a word and an arrowhead, not a colour. Without it there
     is nothing on screen saying the detail exists, which is the same as not having it. */
  .flag summary::after {
    content: '▸ detail';
    color: var(--text-dim);
    font-size: var(--text-xs);
  }

  .flag details[open] summary::after {
    content: '▾ detail';
  }

  .flag summary .badge {
    min-width: 150px;
  }

  .flag .headline {
    font-weight: 600;
  }

  .flag .detail {
    margin: 4px 0 0 160px;
    max-width: 90ch;
  }

  .badge {
    display: inline-block;
    border: 1px solid var(--line-strong);
    border-radius: 4px;
    padding: 0 6px;
    white-space: nowrap;
  }

  .flag.caution .badge {
    border-color: var(--warn);
    color: var(--warn);
  }

  .flag.blocking .badge {
    border-color: var(--danger);
    color: var(--danger);
  }

  .not-started {
    margin: 0;
    display: flex;
    gap: 8px;
    align-items: baseline;
    flex-wrap: wrap;
    color: var(--text-dim);
  }

  .not-started .badge {
    border-color: var(--accent);
    color: var(--accent);
  }

  .figures {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
    gap: 12px;
  }

  .figure-cell {
    display: flex;
    flex-direction: column;
    gap: 2px;
    background: var(--bg-raised);
    border: 1px solid var(--line);
    border-radius: 6px;
    padding: 8px 10px;
  }

  .big {
    font-size: 1.35rem;
  }

  .classes .letter {
    font-size: 1.15rem;
  }

  .classes tr.zero td {
    /* Dimmed, never hidden. A class at zero is the most important row on this table. */
    color: var(--text-dim);
  }

  .ceiling {
    gap: 6px;
  }

  .ceiling .num {
    min-width: 4ch;
    text-align: right;
  }

  .bar {
    display: inline-block;
    width: 90px;
    height: 7px;
    background: var(--bg-input);
    border: 1px solid var(--line);
    border-radius: 3px;
    overflow: hidden;
  }

  .bar .fill {
    display: block;
    height: 100%;
    background: var(--accent-dim);
  }

  .tag {
    display: inline-block;
    border: 1px solid var(--line-strong);
    border-radius: 4px;
    padding: 0 5px;
    font-size: var(--text-xs);
  }

  .tag.warn {
    border-color: var(--warn);
    color: var(--warn);
  }

  tr.sub th {
    padding-left: 22px;
    font-weight: 400;
  }

  caption {
    text-align: left;
    padding: 0 0 6px;
  }

  dl.provenance {
    display: grid;
    grid-template-columns: 140px 1fr;
    gap: 2px 10px;
    margin: 0;
  }

  dl.provenance dt {
    color: var(--text-dim);
  }

  dl.provenance dd {
    margin: 0;
    overflow-wrap: anywhere;
  }

  ul {
    margin: 0;
    padding-left: 18px;
  }

  h4 {
    margin: 6px 0 0;
    color: var(--text-dim);
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }
</style>
